import { Button, Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@1trading/react';
import { ShieldCheck, X } from 'lucide-react';
import { Controller, type UseFormReturn } from 'react-hook-form';
import { StatusChip } from '@/components/ui/status-chip';
import { GENDER_OPTIONS, IDENTIFICATION_TYPE_OPTIONS, ISO_COUNTRY_CODE_LENGTH } from '@/services/api/fiu-utr';
import type { FiuUtrFormValues } from './case-form';

interface ClientIdentificationSectionProps {
  form: UseFormReturn<FiuUtrFormValues>;
  prefilledFromSumsub: boolean;
}

// Sumsub-prefilled goAML identification overrides. Radix selects are not natively
// clearable, so a clear button appears beside each select while it holds a value
// (blank = let the filing source the value from Sumsub).
export const ClientIdentificationSection = ({ form, prefilledFromSumsub }: ClientIdentificationSectionProps) => {
  const issueCountryError = form.formState.errors.identificationIssueCountry?.message;
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold">3. Client identification</h2>
        {prefilledFromSumsub ? (
          <StatusChip className="px-2 py-0.5 text-xs" icon={ShieldCheck} variant="success">
            Prefilled from Sumsub KYC
          </StatusChip>
        ) : null}
      </div>
      <p className="max-w-[900px] text-sm text-muted-foreground">
        Prefilled from the user's KYC (Sumsub) and mapped to goAML codes. Review and edit if anything is wrong; leave a field blank to let the filing source it.
      </p>
      <div className="flex max-w-[900px] flex-col gap-3 sm:flex-row">
        <Controller
          control={form.control}
          name="identificationType"
          render={({ field }) => (
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="fiu-doc-type">Document type</Label>
              <div className="flex items-center gap-1">
                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                  <SelectTrigger className="w-full" id="fiu-doc-type">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {IDENTIFICATION_TYPE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {field.value ? (
                  <Button aria-label="Clear document type" onClick={() => field.onChange(null)} size="icon" type="button" variant="ghost">
                    <X className="size-4" />
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        />
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="fiu-doc-number">Document number</Label>
          <Input id="fiu-doc-number" {...form.register('identificationNumber')} />
        </div>
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="fiu-issue-country">Issue country (ISO-2)</Label>
          <Input id="fiu-issue-country" maxLength={ISO_COUNTRY_CODE_LENGTH} {...form.register('identificationIssueCountry')} />
          {issueCountryError ? <p className="text-sm text-red-500">{issueCountryError}</p> : null}
        </div>
      </div>
      <div className="flex max-w-[900px] flex-col gap-3 sm:flex-row">
        <Controller
          control={form.control}
          name="gender"
          render={({ field }) => (
            <div className="flex flex-1 flex-col gap-1.5">
              <Label htmlFor="fiu-gender">Gender</Label>
              <div className="flex items-center gap-1">
                <Select onValueChange={field.onChange} value={field.value ?? ''}>
                  <SelectTrigger className="w-full" id="fiu-gender">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {GENDER_OPTIONS.map((gender) => (
                      <SelectItem key={gender} value={gender}>
                        {gender}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {field.value ? (
                  <Button aria-label="Clear gender" onClick={() => field.onChange(null)} size="icon" type="button" variant="ghost">
                    <X className="size-4" />
                  </Button>
                ) : null}
              </div>
            </div>
          )}
        />
        <div className="flex flex-1 flex-col gap-1.5">
          <Label htmlFor="fiu-client-email">Client email</Label>
          <Input id="fiu-client-email" {...form.register('email')} />
        </div>
      </div>
    </section>
  );
};
ClientIdentificationSection.displayName = 'ClientIdentificationSection';
